import { createFeatureSelector, createSelector } from '@ngrx/store';
import { handleActions } from 'redux-actions';
import { GlobalSearch } from './global-search.model';
import { DocumentsSearch } from './document-search.model';
import {
  ActionsEnum,
  SearchDocumentsError,
  SearchDocumentsSuccess,
  SearchGlobally,
  SearchGloballyError,
  SearchGloballySuccess,
  SearchRecentDocumentsError,
  SearchRecentDocumentsSuccess,
} from './search-data.actions';
import { RecentDocumentsSearch } from './recent-documents-search.model';
import { ISearchParams } from './search-data.data';

// feature name
export const FEATURE_NAME = 'search-data';

// state interface
export interface IState {
  globalSearch: GlobalSearch;
  globalSearchParams: ISearchParams;
  documentsSearch: DocumentsSearch;
  recentDocuments: RecentDocumentsSearch;
  globalFetched: boolean;
  documentsFetched: boolean;
  recentDocumentsFetched: boolean;
  error: any[];
}

// initial states of store
export const initialState: IState = {
  globalSearch: null,
  globalSearchParams: null,
  documentsSearch: null,
  recentDocuments: null,
  globalFetched: false,
  documentsFetched: false,
  recentDocumentsFetched: false,
  error: null,
};

// reducer
const reducerFn = handleActions(
  {
    [ActionsEnum.SEARCH_GLOBALLY]: function (
      state: IState,
      { payload }: SearchGlobally
    ): IState {
      const { params } = payload;

      return {
        ...state,
        globalSearch: null,
        globalSearchParams: params,
        globalFetched: false,
        error: null,
      };
    },
    [ActionsEnum.SEARCH_GLOBALLY_SUCCESS]: function (
      state: IState,
      { payload }: SearchGloballySuccess
    ): IState {
      const { globalSearch } = payload;

      return {
        ...state,
        globalSearch,
        globalFetched: true,
        error: null,
      };
    },
    [ActionsEnum.SEARCH_GLOBALLY_ERROR]: function (
      state: IState,
      { payload }: SearchGloballyError
    ): IState {
      const { error } = payload;

      return {
        ...state,
        globalSearch: null,
        globalFetched: true,
        error,
      };
    },
    [ActionsEnum.CLEAR_GLOBAL_SEARCH]: function (state: IState): IState {
      return {
        ...state,
        globalSearch: null,
        globalSearchParams: null,
        globalFetched: false,
        error: null,
      };
    },
    [ActionsEnum.SEARCH_DOCUMENTS]: function (state: IState): IState {
      return {
        ...state,
        documentsSearch: null,
        documentsFetched: false,
        error: null,
      };
    },
    [ActionsEnum.SEARCH_DOCUMENTS_SUCCESS]: function (
      state: IState,
      { payload }: SearchDocumentsSuccess
    ): IState {
      const { documentsSearch } = payload;

      return {
        ...state,
        documentsSearch,
        documentsFetched: true,
        error: null,
      };
    },
    [ActionsEnum.SEARCH_DOCUMENTS_ERROR]: function (
      state: IState,
      { payload }: SearchDocumentsError
    ): IState {
      const { error } = payload;

      return {
        ...state,
        documentsSearch: null,
        documentsFetched: true,
        error,
      };
    },
    [ActionsEnum.CLEAR_DOCUMENTS_SEARCH]: function (state: IState): IState {
      return {
        ...state,
        documentsSearch: null,
        documentsFetched: false,
        error: null,
      };
    },
    [ActionsEnum.SEARCH_RECENT_DOCUMENTS]: function (state: IState): IState {
      return {
        ...state,
        recentDocuments: null,
        recentDocumentsFetched: false,
        error: null,
      };
    },
    [ActionsEnum.SEARCH_RECENT_DOCUMENTS_SUCCESS]: function (
      state: IState,
      { payload }: SearchRecentDocumentsSuccess
    ): IState {
      const { recentDocuments } = payload;

      return {
        ...state,
        recentDocuments,
        recentDocumentsFetched: true,
        error: null,
      };
    },
    [ActionsEnum.SEARCH_RECENT_DOCUMENTS_ERROR]: function (
      state: IState,
      { payload }: SearchRecentDocumentsError
    ): IState {
      const { error } = payload;

      return {
        ...state,
        recentDocuments: null,
        recentDocumentsFetched: true,
        error,
      };
    },
    [ActionsEnum.CLEAR_SEARCH_RECENT_DOCUMENTS]: function (
      state: IState
    ): IState {
      return {
        ...state,
        recentDocuments: null,
        recentDocumentsFetched: false,
        error: null,
      };
    },
  } as any,
  initialState
);

// required for AOT
export function reducer(state: IState = initialState, action: any) {
  return reducerFn(state, action);
}

// selectors
export const selectFeature = createFeatureSelector<IState>(FEATURE_NAME);

export const getGlobalSearch = createSelector(
  selectFeature,
  (state: IState) => state.globalSearch
);

export const getGlobalSearchParams = createSelector(
  selectFeature,
  (state: IState) => state.globalSearchParams
);

export const getDocumentsSearch = createSelector(
  selectFeature,
  (state: IState) => state.documentsSearch
);

export const getRecentDocuments = createSelector(
  selectFeature,
  (state: IState) => state.recentDocuments
);

export const getGlobalFetched = createSelector(
  selectFeature,
  (state: IState) => state.globalFetched
);

export const getDocumentsFetched = createSelector(
  selectFeature,
  (state: IState) => state.documentsFetched
);

export const getRecentDocumentsFetched = createSelector(
  selectFeature,
  (state: IState) => state.recentDocumentsFetched
);

export const getError = createSelector(
  selectFeature,
  (state: IState) => state.error
);
